/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { Tooltip } from "@duxton-web/ui"
import { EditableFilled, MoreInfoOutlined } from "@duxton-web/icons"
import React from "react"
import {
  Review,
  ReviewReply,
  ReviewReplyReason,
} from "types/api/menuFeedback.type"
import { ActionType } from "types/models/feedback"
import useFEConfig from "hooks/useFEConfig"
import { useTranslationsMap } from "common/utils/i18n"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import { canEditReply } from "pages/feedback/utils"

type ReplyContentProp = {
  review: Review
  replyContent: ReviewReply
  onClick(actionType: ActionType, review: Review): void
}

const ReplyContent: React.FC<ReplyContentProp> = ({
  review,
  replyContent,
  onClick,
}) => {
  const textMap = useTranslationsMap({
    expiredEdit: "feedback.tooltips.expiredEdit",
    maximumEdit: "feedback.tooltips.maximumEdit",
  })

  const feConfig = useFEConfig({ merchantID: review?.merchantID })
  const canStillReply = canEditReply(review)

  const durationLimit =
    feConfig.data?.reviewReplyConfig?.editDurationLimitInHours || 0
  let messageContent = ""
  switch (replyContent.uneditableReason) {
    case ReviewReplyReason.ReviewUnreplyableReasonEditCountLimitReached:
      messageContent = textMap.maximumEdit
      break
    case ReviewReplyReason.ReviewUnreplyableReasonEditDurationLimitReached:
      messageContent = textMap.expiredEdit.replace(/<x>/gi, `${durationLimit}`)
      break
    default:
      break
  }

  const handleClickEdit = () => {
    TroyWebTracker.sendEvent({
      category: EventCategory.feedbackHomePage,
      action: EventAction.editReply,
      labels: {
        REVIEW_ID: review?.reviewID,
        REVIEW_RATING: review?.rating,
        REPLY_MESSAGE: review?.reviewReplies?.[0]?.description,
      },
    })
    onClick(ActionType.EDIT_REPLY, review)
  }

  const renderIcon = () => {
    if (canStillReply && messageContent.length === 0) {
      return <EditableFilled data-testid="iconEdit" onClick={handleClickEdit} />
    }

    if (!canStillReply && messageContent.length === 0) {
      return <MoreInfoOutlined data-testid="iconInfo" />
    }

    return (
      <Tooltip placement="bottom" overlay={<p>{messageContent}</p>}>
        <MoreInfoOutlined data-testid="iconInfo" />
      </Tooltip>
    )
  }

  return (
    <div
      css={css({
        background: "#f5f5f5",
        borderRadius: "8px",
        padding: "8px",
        marginTop: "12px",
        color: "#707070",
        fontSize: "12px",
        lineHeight: "18px",
        position: "relative",
      })}
    >
      <div
        css={css({
          position: "absolute",
          right: "10px",
        })}
      >
        {renderIcon()}
      </div>
      <p
        css={css({
          paddingRight: "10px",
        })}
      >
        {replyContent.description}
      </p>
    </div>
  )
}

export default React.memo<ReplyContentProp>(ReplyContent)
